import React, {
  ReactElement,
  useState,
  useEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Route, RouteComponentProps } from 'react-router-dom';
import {
  connectHits,
} from 'react-instantsearch-dom';
import axios, { AxiosError, AxiosResponse } from 'axios';
import Swal from 'sweetalert2';

import { isEqual } from 'lodash';
import { faPlay, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormikHelpers } from 'formik';
import {
  ALGOLIA_SELLER_CONTACTS_INDEX,
  PROFILES_ADMIN_PREFIX,
  SELLER_PREFIX,
  USER_ADMIN_PREFIX,
} from '../../../utils/constants';
import AdminURLSync from '../AdminURLSync';
import { SellerContact } from '../../../structures/models/seller';
import useModal from '../../../utils/hooks/useModal';
import ConfirmationModal from '../widgets/ConfirmationModal';
import useBulkSelected from '../../../utils/hooks/useBulkSelected';
import EditSellerContact from '../../forms/seller-contacts/edit-seller-contact';
import routes from '../../../utils/routes';
import { LaravelValidationErrors } from '../../../structures/responses/errors';
import modifyDeleteErrorMessage from '../../../utils/sellerContacts';
import UserSVG from './assets/UserSVG';
import ProfileSVG from './assets/ProfileSVG';
import EditSVG from './assets/EditSVG';
import PhoneNumber from './assets/PhoneNumber';
import { SearchState } from './interfaces';
import AdminDashboardTableHits from './AdminDashboardTableHits';
import AdminDashboardTableHit from './AdminDashboardTableHit';

interface HitProps extends SellerContact {
  selected: Array<number>,
  toggleSelected: (id: number) => void,
  editCallback: (contact: SellerContact) => void,
}

const Hit: React.FC<HitProps> = ({
  name,
  id,
  seller_id,
  email,
  phone,
  phone_extn,
  alternate_phone,
  alternate_phone_extn,
  contact_preference,
  contact_preference_alternate,
  best_time_to_call,
  created_at,
  updated_at,
  is_primary,
  selected,
  toggleSelected,
  editCallback,
}) => {
  const [isHidden, setIsHidden] = useState(false);
  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
  } = useModal();

  const updateModal = (type: string) => {
    switch (type) {
      case 'delete':
        setModalData({
          message: `Would you like to delete this seller contact for ${name}?`,
          onConfirmAsync: () => {
            return new Promise((resolve, reject) => {
              axios.delete(routes.apiSellerContactsDelete(id))
                .then(({ data }) => {
                  resolve(data.message);
                })
                .catch((e: AxiosError) => {
                  // Get errors from response.
                  const catchErrors: object|undefined = e.response?.data?.errors;

                  // Modify error message.
                  const newCatchErrorMessage: string = modifyDeleteErrorMessage(catchErrors);

                  reject(newCatchErrorMessage || e);
                });
            });
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => {
            setIsHidden(true);
          },
        });
        break;
      default:
        setModalData({
          message: '',
          onClose: () => {
          },
        });
    }
  };

  const onEdit = useCallback(() => {
    editCallback(
      {
        id,
        seller_id,
        name,
        email,
        phone,
        phone_extn,
        alternate_phone,
        alternate_phone_extn,
        contact_preference,
        contact_preference_alternate,
        best_time_to_call,
        created_at,
        updated_at,
      },
    );
  }, [
    editCallback,
    id,
    seller_id,
    name,
    email,
    phone,
    phone_extn,
    alternate_phone,
    alternate_phone_extn,
    contact_preference,
    best_time_to_call,
    created_at,
    updated_at,
  ]);

  // TODO: update `any`
  const values: any = [
    {
      key: 'edit-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <button
          className="edit contact btn btn-admin-button-gray d-flex align-items-center text-uppercase"
          onClick={onEdit}
        >
          <EditSVG />
        </button>
      ),
    },
    {
      key: 'delete-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <a
          href="#"
          className="mx-2"
          aria-label={`Delete contact: ${name}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
    {
      key: 'name',
      value: (
        <div className="d-flex">
          <p className={`m-0${is_primary ? ' font-weight-bold' : ''}`}>
            {`${name}${is_primary ? ' (primary)' : ''}`}
          </p>
        </div>
      ),
    },
    {
      key: 'phone',
      value: phone ? (
        <div className="d-flex">
          {
            phone
              ? (
                <PhoneNumber
                  phone={phone}
                  phone_extn={phone_extn}
                  linkClassName="text-nowrap text-table-light-gray m-0"
                  textClassName="text-nowrap text-table-light-gray m-0"
                />
              ) : (
                <></>
              )
          }
        </div>
      ) : <></>,
    },
    {
      key: 'alternate_phone',
      value: alternate_phone ? (
        <div className="d-flex">
          {
            alternate_phone
              ? (
                <PhoneNumber
                  phone={alternate_phone}
                  phone_extn={alternate_phone_extn}
                  linkClassName="text-nowrap text-table-light-gray m-0"
                  textClassName="text-nowrap text-table-light-gray m-0"
                />
              ) : (
                <></>
              )
          }
        </div>
      ) : <></>,
    },
    {
      key: 'email',
      value: (
        <div className="d-flex">
          <a
            className="text-table-light-gray m-0"
            href={`mailto:${email}`}
          >
            {email}
          </a>
        </div>
      ),
    },
    {
      key: 'contact_preference',
      value: (
        <div className="d-flex">
          <p className="text-table-light-gray m-0 text-capitalize">
            {contact_preference.join(', ')}
          </p>
        </div>
      ),
    },
    {
      key: 'best_time_to_call',
      value: (
        <div className="d-flex">
          <p className="text-table-light-gray m-0 text-capitalize">{best_time_to_call}</p>
        </div>
      ),
    },
    {
      key: 'edit',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <button
          className="edit widescreen contact btn btn-admin-button-gray d-flex align-items-center text-uppercase"
          onClick={onEdit}
        >
          <EditSVG />
          <span className="ml-1">Edit Contact</span>
        </button>
      ),
    },
    {
      key: 'delete',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <a
          href="#"
          className="widescreen mx-2"
          aria-label={`Delete contact: ${name}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
  ];

  return (
    <>
      {!isHidden && (
        <AdminDashboardTableHit
          values={values}
          id={id}
          selected={selected}
          toggleSelected={toggleSelected}
        />
      )}
      {!isHidden && (
        <ConfirmationModal
          show={showModal}
          setShow={setShowModal}
          {...modalData}
        />
      )}
    </>
  );
};

const Hits: React.FC<{
  hits: any,
  selected: Array<number>,
  toggleSelected: (id: number) => void,
  editCallback: (contact: SellerContact) => void,
}> = ({
  hits,
  selected,
  toggleSelected,
  editCallback,
}) => {
  return (
    <AdminDashboardTableHits
      hits={hits}
      Hit={Hit}
      selected={selected}
      toggleSelected={toggleSelected}
      editCallback={editCallback}
    />
  );
};

const CustomHits = connectHits(Hits);

interface Props extends RouteComponentProps {
  profile: {
    id: number | string,
    username: string,
    user_id: number | string,
    legacy_id?: number | string | null,
    external_breeder_id?: number | string | null,
    name: string,
    phone: number | string,
    phone_extn?: number | string | null,
    email: string,
    city: string,
    state: string,
    zip: string | number,
    address_line_1: string,
    address_line_2?: string,
    notes?: string,
    slug: string,
    ip?: string,
  },
}

const AdminSingleProfileContacts: React.FC<Props> = ({
  history,
  location,
  match,
  profile,
}): ReactElement => {
  const loadInitialSearchState = (browserMatch) => {
    const numericFilters: string[] = [];
    const { params } = browserMatch;
    const { id } = params as { id: number };
    if (id) {
      numericFilters.push(`seller_id = ${id}`);
    }
    return {
      configure: {
        facetFilters: [],
        numericFilters,
      },
      query: '',
    };
  };
  const [externalSearchState, setExternalSearchState] = useState<SearchState>(
    loadInitialSearchState(match),
  );
  const [currentlyEditing, setCurrentlyEditing] = useState<SellerContact | undefined>(undefined);

  const {
    bulkAction,
    setBulkAction,
    updateBulkSelected,
    bulkSelected,
    toggleAllSelected,
    allSelected,
    setHits,
    clearBulkSelected,
  } = useBulkSelected();

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
    modalFormState,
    setModalFormState,
    reloadPage,
  } = useModal();

  const performBulkAction = useCallback((action: string) => {
    if (bulkSelected.length < 1) {
      setModalData({
        message: 'No Contacts have been selected. Please, select one or more Contacts to bulk edit.',
        isSimple: true,
        onConfirm: () => {
          setModalData({
            message: '',
            onClose: () => {
            },
            isSimple: true,
          });
        },
        onClose: () => {
          setModalData({
            message: '',
            onClose: () => {
            },
            isSimple: true,
          });
        },
      });
    } else {
      switch (action) {
        case 'delete':
          setModalData({
            message: `Would you like to delete ${bulkSelected.length} seller contacts?`,
            onConfirmAsync: () => {
              return new Promise((res, rej) => {
                axios.delete(routes.apiSellerContactsDelete(bulkSelected.join(',')))
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'flag':
          setModalData({
            message: (
              <>
                <p>
                  Would you like to delete
                  {' '}
                  {bulkSelected.length}
                  {' '}
                  user accounts?
                </p>
                <ul>
                  <li>The associated SellerContact(s) will be permanently deleted.</li>
                  <li>
                    The associated Litters and Listings that each of
                    those SellerContact(s) may have had will be permanently deleted.
                  </li>
                  <li>
                    The Seller itself will be permanently deleted.
                  </li>
                </ul>
              </>
            ),
            onConfirmAsync: () => {
              return new Promise((res, rej) => {
                axios.delete(`contact/${bulkSelected.join(',')}`) // TODO: Update URL
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        default:
          setModalData({
            message: '',
            onClose: () => {
            },
            isSimple: true,
          });
      }
    }
  }, [bulkSelected, modalFormState, reloadPage]);

  useEffect(() => {
    const { value } = bulkAction;
    if (value) {
      performBulkAction(value);
    }
  }, [bulkAction]);

  useEffect(() => {
    const params = match.params as { id: string };
    const { configure } = externalSearchState;
    if (configure) {
      const { numericFilters } = configure;
      if (!isEqual(numericFilters, [`seller_id = ${params.id}`])) {
        setExternalSearchState({
          ...externalSearchState,
          configure: {
            ...externalSearchState.configure,
            numericFilters: [`seller_id = ${params.id}`],
          },
        });
      }
    }
  }, [match]);

  const scrollPage = (bottom: number) => {
    window.scrollTo({
      left: 0,
      top: bottom,
      behavior: 'smooth',
    });
  };

  const tableHeaderRowValues = [
    {
      key: 'contact_name',
      value: 'Contact Name',
      sort: {
        asc: ALGOLIA_SELLER_CONTACTS_INDEX,
        desc: `${ALGOLIA_SELLER_CONTACTS_INDEX}_name_za`,
      },
    },
    {
      key: 'contact_phone',
      value: 'Contact Phone',
    },
    {
      key: 'alternate_phone',
      value: 'Alternate Phone',
    },
    {
      key: 'contact_email',
      value: 'Contact Email',
      sort: {
        asc: `${ALGOLIA_SELLER_CONTACTS_INDEX}_email_asc`,
        desc: `${ALGOLIA_SELLER_CONTACTS_INDEX}_email_desc`,
      },
    },
    {
      key: 'contact_preference',
      value: 'Preference',
    },
    {
      key: 'best_time_to_call',
      value: 'Best Time to Call',
    },
  ];

  const TitleComponent = () => (
    <div className="d-flex align-items-center">
      <h1 className="d-flex align-items-center text-capitalize">
        <span
          className="d-flex"
          style={{
            width: '40px',
            height: '40px',
          }}
        >
          <ProfileSVG />
        </span>
        <span className="ml-2">
          {`Profile: ${profile.name}`}
        </span>
      </h1>
    </div>
  );

  const BodyComponent = () => (
    <div className="seller-profile-heading">
      <div className="px-2 px-md-4">
        <div className="d-flex flex-column">
          <div className="d-flex flex-column flex-md-row">
            <p className="d-flex align-items-center mr-2">
              <span
                className="d-flex mr-1"
                style={{
                  width: '15px',
                  height: '15px',
                }}
              >
                <UserSVG />
              </span>
              <span>
                <b>Account Owner: </b>
                <a
                  className="text-black m-0"
                  href={`${USER_ADMIN_PREFIX}/${profile.user_id}`}
                >
                  {profile.username}
                </a>
              </span>
            </p>
            {profile.external_breeder_id && (
              <p>
                <span className="d-none d-md-inline">&emsp;</span>
                <b>Breeder ID: </b>
                {profile.external_breeder_id}
              </p>
            )}
            {profile.ip && (
              <p>
                <span className="d-none d-md-inline">&emsp;</span>
                <b>IP: </b>
                {profile.ip}
              </p>
            )}
          </div>
          {!!profile.phone && (
            <PhoneNumber
              phone={profile.phone}
              phone_extn={profile.phone_extn}
              linkClassName="text-nowrap text-table-light-gray m-0 mb-2 d-block"
              textClassName="text-nowrap text-table-light-gray m-0 mb-2"
            />
          )}
          <p>
            <span>
              {profile.address_line_1 ? `${profile.address_line_1}, ` : ''}
              {profile.address_line_2 ? `${profile.address_line_2}, ` : ''}
            </span>
            <span>
              {`${profile.city ? `${profile.city}, ` : ''}${profile.state ? `${profile.state} ` : ''}${profile.zip || ''}`}
            </span>
          </p>
        </div>
      </div>
      <hr />
      <h2>Contacts</h2>
    </div>
  );

  const setEdit = useCallback((sellerContact: SellerContact) => {
    if (!currentlyEditing) {
      setCurrentlyEditing(sellerContact);
    } else {
      if (currentlyEditing.id !== sellerContact.id) {
        setCurrentlyEditing(sellerContact);
      }
      // If already editing, then scroll to open form
      const table = document.querySelector('.admin-dashboard-table-wrapper table.table');
      if (table) {
        const { bottom } = table.getBoundingClientRect();
        scrollPage(bottom);
      }
    }
  }, [currentlyEditing, scrollPage]);

  const ActionButton = useCallback(() => (
    <>
      <a
        className="mr-2 mb-2 mb-lg-0 btn btn-outline-light border-border-grey button-shadow text-dark-btn-txt"
        href={`${SELLER_PREFIX}${profile.slug}`}
      >
        <span className="mr-2 action-font text-uppercase">
          View Profile Page
        </span>
        <FontAwesomeIcon icon={faPlay} size="xs" />
      </a>
      <a
        className="d-flex align-items-center mr-2 mb-2 mb-lg-0 btn btn-outline-light border-border-grey button-shadow text-dark-btn-txt"
        href={`${PROFILES_ADMIN_PREFIX}/${profile.id}/edit`}
      >
        <span className="mr-2 action-font text-uppercase">
          Edit Profile
        </span>
        <EditSVG />
      </a>
      <a
        className="mr-2 mb-2 mb-lg-0 btn btn-outline-light border-border-grey button-shadow text-dark-btn-txt"
        href={routes.adminSellerRevisionsGet(profile.id)}
      >
        <span className="mr-2 action-font text-uppercase">
          History
        </span>
        <FontAwesomeIcon icon={faPlay} size="xs" />
      </a>
    </>
  ), [profile, currentlyEditing, match, scrollPage]);

  const onSave = (
    contact: SellerContact,
    {
      setErrors,
      setSubmitting,
    }: FormikHelpers<SellerContact>,
    setFormError: (message: string) => void,
    setFormSubmitting: (submitting: boolean) => void,
  ): void => {
    // Tell formik we're submitting
    setSubmitting(true);
    // Tell this component we're submitting
    // Tell the EditSellerContact form we're submitting
    setFormSubmitting(true);

    // Format phone number for submission
    // Creates a new object to avoid mutating the `contact` object
    const data: SellerContact = {
      ...contact,
      phone: contact.phone ? contact.phone.replace(/\D/g, '') : '',
      alternate_phone: contact.alternate_phone ? contact.alternate_phone.replace(/\D/g, '') : '',
    };

    let promise;
    if (data.id) {
      promise = axios.put(routes.apiSellerContactsUpdate(contact.id), data);
    } else {
      promise = axios.post(routes.API_SELLER_CONTACTS_POST, data);
    }

    promise.then((res: AxiosResponse<{ data: SellerContact }>) => {
      if (res.status >= 200 || res.status < 300) {
        setCurrentlyEditing(undefined);

        if (data.id) {
          Swal.fire(
            'Success',
            'Contact updated successfully!',
            'success',
          )
            .then(() => window.location.reload());
        } else {
          Swal.fire(
            'Success',
            'Contact created successfully!',
            'success',
          )
            .then(() => window.location.reload());
        }
      }
    })
      .catch((error: AxiosError) => {
        const statusCode: number | undefined = error.response?.status;

        if (statusCode && statusCode === 422) {
          const { errors }: { errors: LaravelValidationErrors } = error.response?.data;
          setFormError('Please check your inputs and try again.');

          // Sets formik errors
          setErrors(errors);
        } else if (statusCode && statusCode === 419) {
          setFormError('This form must be refreshed.');
        } else {
          setFormError('An unknown error occurred. Please try again later.');
        }

        // If error, then we need to end the submission. We don't need to do this on success
        // because the form is closed. If we put this in the finally() block, we get a react
        // memory leak.
        setSubmitting(false);
        setFormSubmitting(false);
      });
  };

  const onCancel = () => {
    setCurrentlyEditing(undefined);
  };

  return (
    <>
      <AdminURLSync
        TitleComponent={TitleComponent}
        BodyComponent={BodyComponent}
        ActionButton={ActionButton}
        numEmptyStartHeaders={2}
        numEmptyEndHeaders={2}
        setBulkAction={setBulkAction}
        bulkAction={bulkAction}
        setHits={setHits}
        allSelected={allSelected}
        toggleAllSelected={toggleAllSelected}
        clearBulkSelected={clearBulkSelected}
        bulkActions={[
          {
            label: 'Flag For Review',
            value: 'flag',
          },
          {
            label: 'Delete',
            value: 'delete',
          },
        ]}
        externalQueryParams={[]}
        history={history}
        location={location}
        match={match}
        externalSearchState={externalSearchState}
        tableHeaderRowValues={tableHeaderRowValues}
        indexName={ALGOLIA_SELLER_CONTACTS_INDEX!}
        customHits={(
          <CustomHits
            selected={bulkSelected}
            toggleSelected={updateBulkSelected}
            editCallback={setEdit}
          />
        )}
        attributes={[]}
        dateAttributes={[]}
        tabs={[]}
      />
      <ConfirmationModal
        show={showModal}
        setShow={setShowModal}
        modalFormState={modalFormState}
        clearBulkSelected={clearBulkSelected}
        {...modalData}
      />
      {
        !!currentlyEditing && (
          <EditSellerContact
            contact={currentlyEditing}
            onSave={onSave}
            onCancel={onCancel}
            useScroll
          />
        )
      }
    </>
  );
};

export default AdminSingleProfileContacts;

if (document.getElementById('react-admin-dashboard-table-contacts')) {
  const element = document.getElementById('react-admin-dashboard-table-contacts');
  const profile = element?.dataset?.profile
    ? JSON.parse(element.dataset.profile as string)
    : null;

  ReactDOM.render(
    <BrowserRouter>
      <Route
        path={`${PROFILES_ADMIN_PREFIX}/:id`}
        render={(props) => <AdminSingleProfileContacts profile={profile} {...props} />}
      />
    </BrowserRouter>,
    element,
  );
}
